/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useCallback, useMemo } from "react";

import { usePlayerComponents } from "../../../../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import SeekForwardIcon from "../../../../../../shared/icons/player/Skip_back_10.svg";
import SeekBackIcon from "../../../../../../shared/icons/player/Skip_forward_10.svg";
import {
  usePlayerController,
  usePlayerSettings,
  usePlayerTimer,
} from "../../../../../BasePlayer";

import "./SeekButton.scss";

interface SeekButtonProps {
  isBackSeek?: boolean;
}

export const SeekButton = (props: SeekButtonProps) => {
  const { isBackSeek } = props;

  const { player } = usePlayerController();

  const {
    timer: { isLive, atLiveEdge, isInfinityStream },
  } = usePlayerTimer();

  const {
    ui: { tooltipOffset, seekJumpSeconds },
  } = usePlayerSettings();

  const { t } = usePlayerTranslation();
  const { Button } = usePlayerComponents();

  const show = useMemo(() => {
    // Infinity stream is not seekable
    if (isInfinityStream) {
      return false;
    }

    // Not allow to seek forward at live edge
    if (!isBackSeek) {
      return !(isLive && atLiveEdge);
    }

    return true;
  }, [atLiveEdge, isBackSeek, isLive, isInfinityStream]);

  const onClick = useCallback(() => {
    isBackSeek
      ? player.seekBackward(seekJumpSeconds)
      : player.seekForward(seekJumpSeconds);
  }, [isBackSeek, player, seekJumpSeconds]);
  const label = t(
    isBackSeek ? "PLAYER_TOOLTIP_SEEK_BACK" : "PLAYER_TOOLTIP_SEEK_FORWARD",
  );

  if (!show) {
    return <></>;
  }

  return (
    <Button
      variant="secondary"
      className="SeekButton"
      onClick={onClick}
      tooltip={label}
      tooltipOffset={tooltipOffset}
      aria-label={label}
      iconOnly
    >
      <div className="SeekButton__Container">
        {isBackSeek ? (
          <SeekBackIcon aria-hidden="true" focusable="false" />
        ) : (
          <SeekForwardIcon aria-hidden="true" focusable="false" />
        )}
      </div>
    </Button>
  );
};
